import { ApiErrorMessages } from '@/components/element/ApiErrorMessages';
import { InputPrice } from '@/components/element/Form/InputPrice';
import { ScrollTop } from '@/components/element/ScrollTop';
import { WorkspaceLayout } from '@/components/layout/WorkspaceLayout';
import { PaymentSchedulesElement } from '@/features/w/[identifier]/teams/[team_id]/purchase_orders/[purchase_order_id]/payment_schedules/{components}/PaymentSchedulesElement';
import { usePurchaseOrderInvalidate } from '@/hooks/api/0570_purchase_order';
import { usePaymentSchedules, usePaymentSchedulesInvalidate } from '@/hooks/api/0680_payment_schedules';
import { useCreatePaymentSchedule } from '@/hooks/api/0690_create_payment_schedule';
import { PaymentScheduleLogic, PurchaseOrderLogic, TeamMemberLogic } from '@/hooks/api/models/logics';
import { useLoading } from '@/providers/LoadingProvider';
import { useNavState } from '@/providers/NavStateProvider';
import { useCurrentPurchaseOrder } from '@/providers/PurchaseOrderProvider';
import { useCurrentTeamMember } from '@/providers/TeamMemberProvider';
import { useEffect, useMemo, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { Link, useLocation } from 'react-router-dom';
import { useCreatePurchaseOrderAccounting } from '@/hooks/api/0730_create_purchase_order_accounting';
import { usePurchaseOrderAccountingsInvalidate } from '@/hooks/api/0720_purchase_order_accountings';
import { PurchaseOrderAccountingsElement } from '@/features/w/[identifier]/teams/[team_id]/purchase_orders/[purchase_order_id]/payment_schedules/{components}/PurchaseOrderAccountingsElement';
import { ManualModeEditElement } from '@/features/w/[identifier]/teams/[team_id]/purchase_orders/[purchase_order_id]/payment_schedules/{components}/ManualModeEditElement';

type FormInputs = {
  identifier: string;
  team_id: number;
  purchase_order_id: number;
  year_month: string;
  monthly_amount?: number;
  spot_amount?: number;
};

export const PaymentSchedules = () => {
  const { _w_, _t_ } = useCurrentTeamMember();
  const { _po_ } = useCurrentPurchaseOrder();
  const { setTitle, setBreadcrumbs } = useNavState();
  useEffect(() => setTitle(`費用`), []);
  useEffect(
    () =>
      setBreadcrumbs([
        {
          label: `プロジェクト（費用） - ${_t_!.name}`,
          path: `/w/${_w_!.identifier}/teams/${_t_!.id}/purchase_orders`,
        },
        {
          label: `プロジェクト（費用）詳細`,
          path: `/w/${_w_!.identifier}/teams/${_t_!.id}/purchase_orders/${_po_!.id}`,
        },
      ]),
    []
  );

  return (
    <WorkspaceLayout>
      <ScrollTop />

      <div className="container-fluid py-4">
        <div className="row">
          <div className="col-12 mx-auto">
            <_PaymentSchedules />

            <_PurchaseOrderAccountings />
          </div>
        </div>
      </div>
    </WorkspaceLayout>
  );
};

const _PaymentSchedules = () => {
  const { setShowLoading } = useLoading();
  const { _w_, _t_, me, meTeamMember } = useCurrentTeamMember();

  const location = useLocation();
  const { _po_, mePurchaseOrderMember } = useCurrentPurchaseOrder();
  const { data: paymentSchedules } = usePaymentSchedules({
    identifier: _w_!.identifier,
    team_id: _t_!.id,
    id: _po_!.id,
  });
  const { mutate, data, isPending, error } = useCreatePaymentSchedule();
  const { invalidate } = usePaymentSchedulesInvalidate({ identifier: _w_!.identifier, team_id: _t_!.id, id: _po_!.id });
  const { invalidate: invalidatePurchaseOrder } = usePurchaseOrderInvalidate({
    identifier: _w_!.identifier,
    team_id: _t_!.id,
    id: _po_!.id,
  });
  const [ymOptions, setYmOptions] = useState<string[]>([]);

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormInputs>({
    mode: 'onBlur',
    criteriaMode: 'all',
    defaultValues: {
      identifier: _w_!.identifier,
      team_id: _t_!.id,
      purchase_order_id: _po_!.id,
    },
  });

  const isEditable = useMemo(() => {
    if (meTeamMember && TeamMemberLogic.hasApproverRole(meTeamMember)) {
      return true;
    } else {
      return !_po_!.closed;
    }
  }, [meTeamMember, _po_]);

  useEffect(() => {
    if (paymentSchedules) {
      setYmOptions(PaymentScheduleLogic.yearMonthOptions(_po_!, paymentSchedules));
    }
  }, [paymentSchedules]);

  useEffect(() => setShowLoading(isPending), [isPending]);

  const onSubmit: SubmitHandler<FormInputs> = (data) => {
    setShowLoading(true);
    mutate(data);
  };

  useEffect(() => {
    if (data) {
      invalidate();
      invalidatePurchaseOrder();
    }
  }, [data]);

  return (
    <div className="card mb-4" id="basic-info">
      <div className="card-header d-flex align-items-center">
        <div className="col-md-8">
          <h5 className="mb-1">費用</h5>
          <p className="text-xs mb-0">支払予定と実績の管理</p>
        </div>
      </div>
      <div className="card-body pt-0 row">
        <div className="col-12 col-md-6">
          <div className="form-group">
            <label>プロジェクト名</label>
            <p className="form-text text-muted text-sm ms-1">{_po_!.name}</p>
          </div>
        </div>

        <hr className="horizontal dark my-2" />

        <form onSubmit={handleSubmit(onSubmit)} className="text-start">
          <div className="accordion" id="accordion">
            <div className="accordion-item">
              <h5 className="accordion-header text-sm text-decoration-underline" id="headingOne">
                <button
                  className="accordion-button font-weight-bold collapsed px-0"
                  type="button"
                  data-bs-toggle="collapse"
                  data-bs-target="#collapseOne"
                  aria-expanded="false"
                  aria-controls="collapseOne"
                >
                  支払月を追加する場合はこちら
                  <i className="collapse-close fa fa-plus text-xs pt-1 position-absolute end-0" aria-hidden="true"></i>
                  <i className="collapse-open fa fa-minus text-xs pt-1 position-absolute end-0" aria-hidden="true"></i>
                </button>
              </h5>
              <div id="collapseOne" className="accordion-collapse collapse row" data-bs-parent="#accordion">
                <ApiErrorMessages error={error} />

                <div className="col-12 col-md-3">
                  <div className="form-group">
                    <label>
                      請求月<span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      maxLength={7}
                      className={`form-control ${errors.year_month && 'is-invalid'}`}
                      placeholder={'YYYY/MM'}
                      list="billing-date"
                      disabled={!isEditable}
                      {...register('year_month', {
                        required: '入力して下さい。',
                        pattern: {
                          value: /^[0-9]{4}\/(0[1-9]|1[0-2])$/,
                          message: 'YYYY/MM形式で入力して下さい。',
                        },
                      })}
                    />
                    <datalist id="billing-date">
                      {ymOptions.map((d) => (
                        <option key={`ym-${d}`} value={d}></option>
                      ))}
                    </datalist>
                    <div className="text-xs text-muted mt-1">請求が実施される年月</div>
                  </div>
                </div>
                <div className="col-12 col-md-3">
                  <div className="form-group">
                    <label>一括支払金額（税抜）</label>
                    <div className="input-group me-2">
                      <Controller
                        render={({ field: { onChange, value, ...field } }) => (
                          <InputPrice onChange={onChange} value={value} disabled={!isEditable} />
                        )}
                        control={control}
                        name="spot_amount"
                      />
                    </div>
                    <div className="text-xs text-muted mt-1">納品時等一括で支払う金額</div>
                  </div>
                </div>
                <div className="col-12 col-md-3">
                  <div className="form-group">
                    <label>月額支払金額（税抜）</label>
                    <div className="input-group me-2">
                      <Controller
                        render={({ field: { onChange, value, ...field } }) => (
                          <InputPrice onChange={onChange} value={value} disabled={!isEditable} />
                        )}
                        control={control}
                        name="monthly_amount"
                      />
                    </div>
                    <div className="text-xs text-muted mt-1">&nbsp;</div>
                  </div>
                </div>
                <div className="col-12 col-md-3">
                  <div className="form-group">
                    <label>&nbsp;</label>
                    <button className="btn w-100 btn-icon bg-gradient-dark" disabled={!isEditable}>
                      <i className="fa fa-plus"></i>追加
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>

        <hr className="horizontal dark my-3" />

        <PaymentSchedulesElement />
      </div>
      <div className="card-footer d-flex align-items-center justify-content-end pt-0">
        <Link
          to={
            location?.state?.prevPath
              ? location?.state?.prevPath
              : `/w/${_w_!.identifier}/teams/${_t_!.id}/purchase_orders/${_po_!.id}`
          }
          className="btn btn-outline-secondary"
        >
          案件詳細へ
        </Link>
      </div>
    </div>
  );
};

const _PurchaseOrderAccountings = () => {
  const { setShowLoading } = useLoading();
  const { _w_, _t_, me, meTeamMember } = useCurrentTeamMember();
  const { _po_ } = useCurrentPurchaseOrder();
  const { mutate, data, isPending, error } = useCreatePurchaseOrderAccounting();
  const { invalidate } = usePurchaseOrderAccountingsInvalidate({
    identifier: _w_!.identifier,
    team_id: _t_!.id,
    id: _po_!.id,
  });
  const { invalidate: invalidatePurchaseOrder } = usePurchaseOrderInvalidate({
    identifier: _w_!.identifier,
    team_id: _t_!.id,
    id: _po_!.id,
  });

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormInputs>({
    mode: 'onBlur',
    criteriaMode: 'all',
    defaultValues: {
      identifier: _w_!.identifier,
      team_id: _t_!.id,
      purchase_order_id: _po_!.id,
      spot_amount: 0,
      monthly_amount: 0,
    },
  });

  useEffect(() => setShowLoading(isPending), [isPending]);

  const onSubmit: SubmitHandler<FormInputs> = (data) => {
    setShowLoading(true);
    mutate(data);
  };

  useEffect(() => {
    if (data) {
      invalidate();
      invalidatePurchaseOrder();
    }
  }, [data]);

  return (
    <div className="card mb-4" id="basic-info">
      <div className="card-header d-flex align-items-center">
        <div className="col-md-8">
          <h5 className="mb-1">手動管理モード</h5>
          <p className="text-xs mb-0">
            手動管理モードをONにすると、支払い実績とは独立して管理会計を編集できます。
            <br />
            請求月と計上月を変えたい場合は、手動管理モードにしてください。
            <br />
            一度手動管理モードにすると、元のモードに戻すことはできません。
          </p>
        </div>
      </div>
      <div className="card-body pt-0 row">
        {/*{!_po_!.accounting_manual_mode && PurchaseOrderLogic.canUpdateAccounting(_t_!, _po_!, me!) && <ManualModeEditElement />}*/}
        <ManualModeEditElement />

        {_po_!.accounting_manual_mode && PurchaseOrderLogic.canUpdateAccounting(_t_!, _po_!, me!) && (
          <>
            <hr className="horizontal dark my-3" />
            <form onSubmit={handleSubmit(onSubmit)} className="text-start">
              <div className="row">
                <ApiErrorMessages error={error} />

                <div className="col-12 col-md-3">
                  <div className="form-group">
                    <label>
                      計上月<span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      maxLength={7}
                      className={`form-control ${errors.year_month && 'is-invalid'}`}
                      placeholder={'YYYY/MM'}
                      {...register('year_month', {
                        required: '入力して下さい。',
                        pattern: {
                          value: /^[0-9]{4}\/(0[1-9]|1[0-2])$/,
                          message: 'YYYY/MM形式で入力して下さい。',
                        },
                      })}
                    />
                    <datalist id="billing-date"></datalist>
                    <div className="text-xs text-muted mt-1">計上の対象となる年月</div>
                  </div>
                </div>
                <div className="col-12 col-md-3">
                  <div className="form-group">
                    <label>一括支払金額（税抜）</label>
                    <div className="input-group me-2">
                      <Controller
                        render={({ field: { onChange, value, ...field } }) => (
                          <InputPrice onChange={onChange} value={value} />
                        )}
                        control={control}
                        name="spot_amount"
                      />
                    </div>
                    <div className="text-xs text-muted mt-1">管理会計上の一括で計上する金額</div>
                  </div>
                </div>
                <div className="col-12 col-md-3">
                  <div className="form-group">
                    <label>月額支払金額（税抜）</label>
                    <div className="input-group me-2">
                      <Controller
                        render={({ field: { onChange, value, ...field } }) => (
                          <InputPrice onChange={onChange} value={value} />
                        )}
                        control={control}
                        name="monthly_amount"
                      />
                    </div>
                    <div className="text-xs text-muted mt-1">管理会計上月額で計上する金額</div>
                  </div>
                </div>
                <div className="col-12 col-md-3">
                  <div className="form-group">
                    <label>&nbsp;</label>
                    <button className="btn w-100 btn-icon bg-gradient-dark">
                      <i className="fa fa-plus"></i>追加
                    </button>
                  </div>
                </div>
              </div>
            </form>
          </>
        )}

        {_po_!.accounting_manual_mode && (
          <>
            <hr className="horizontal dark my-3" />

            <PurchaseOrderAccountingsElement />
          </>
        )}
      </div>
    </div>
  );
};
